import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

export type BasicEditorSession = {
  playlistId: string;
  selectedIds: number[];
};

export type SharedEditorsSession = {
  playlistId: string;
  selectedIds: number[];
  expandedCategoryKeys: string[];
};

type Ctx = {
  basic: BasicEditorSession | null;
  setBasicSession: (v: BasicEditorSession | null) => void;
  getSharedEditorState: (playlistId: string) => SharedEditorsSession;
  setSharedEditorState: (
    playlistId: string,
    patch: Partial<Pick<SharedEditorsSession, 'selectedIds' | 'expandedCategoryKeys'>>,
  ) => void;
  clearSharedEditorState: (playlistId: string) => void;
};

const EditorSessionContext = createContext<Ctx | null>(null);

export function EditorSessionProvider({ children }: { children: ReactNode }) {
  const [basic, setBasic] = useState<BasicEditorSession | null>(null);
  const [sharedByPlaylist, setSharedByPlaylist] = useState<
    Record<string, Pick<SharedEditorsSession, 'selectedIds' | 'expandedCategoryKeys'>>
  >({});

  const setBasicSession = useCallback((v: BasicEditorSession | null) => {
    setBasic(v);
    if (v?.playlistId) {
      setSharedByPlaylist((prev) => ({
        ...prev,
        [v.playlistId]: {
          selectedIds: v.selectedIds ?? [],
          expandedCategoryKeys: prev[v.playlistId]?.expandedCategoryKeys ?? [],
        },
      }));
    }
  }, []);

  const getSharedEditorState = useCallback(
    (playlistId: string): SharedEditorsSession => {
      const row = sharedByPlaylist[playlistId];
      return {
        playlistId,
        selectedIds: row?.selectedIds ?? [],
        expandedCategoryKeys: row?.expandedCategoryKeys ?? [],
      };
    },
    [sharedByPlaylist],
  );

  const setSharedEditorState = useCallback(
    (
      playlistId: string,
      patch: Partial<Pick<SharedEditorsSession, 'selectedIds' | 'expandedCategoryKeys'>>,
    ) => {
      setSharedByPlaylist((prev) => {
        const cur = prev[playlistId] ?? { selectedIds: [], expandedCategoryKeys: [] };
        return {
          ...prev,
          [playlistId]: {
            selectedIds: patch.selectedIds ?? cur.selectedIds,
            expandedCategoryKeys: patch.expandedCategoryKeys ?? cur.expandedCategoryKeys,
          },
        };
      });
    },
    [],
  );

  const clearSharedEditorState = useCallback((playlistId: string) => {
    setSharedByPlaylist((prev) => {
      if (!(playlistId in prev)) return prev;
      const next = { ...prev };
      delete next[playlistId];
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({
      basic,
      setBasicSession,
      getSharedEditorState,
      setSharedEditorState,
      clearSharedEditorState,
    }),
    [basic, setBasicSession, getSharedEditorState, setSharedEditorState, clearSharedEditorState],
  );

  return <EditorSessionContext.Provider value={value}>{children}</EditorSessionContext.Provider>;
}

export function useEditorSession(): Ctx {
  const ctx = useContext(EditorSessionContext);
  if (!ctx) throw new Error('useEditorSession requires EditorSessionProvider');
  return ctx;
}

/** Czy użytkownik jest na stronie Basic editor z danym wyborem (jak legacy getBasicEditorUrlEditorContext). */
export function useBasicEditorToolContext(): {
  playlistId: string | null;
  selectedIds: number[];
  gridActive: boolean;
} {
  const { basic } = useEditorSession();
  return {
    playlistId: basic?.playlistId ?? null,
    selectedIds: basic?.selectedIds ?? [],
    gridActive: !!basic?.playlistId,
  };
}
